<script lang="ts">
  import { defineComponent } from 'vue';
  import { ALIGN_MODES, JUSTIFY_MODES } from '@/models/layoutDesigner';

  export default defineComponent({
    name: 'TenantsViewer',
  });
</script>

<script setup lang="ts">
  import { duration } from 'moment';
  import { ref, watch, toRef, computed, toRefs, onMounted, onUnmounted } from 'vue';
  import { useStore } from '@/store';
  import axios from 'axios';

  import Loader from '@/components/common/Loader.vue';
  import { FLOOR_NAME_TYPES } from '@/constants/tenants';
  import {
    DEFAULT_TENANTS_NAME_SEPARATOR,
    NAME_FORMAT,
    NAME_SOURCE,
    RENTAL_STATUS,
    FLOOR_NUMBER_MODE,
    ROW_VERTICAL_ALIGN,
  } from '@/constants/tenants';
  import { ORGANISATION_WAIT_INTERVAL_MS, ORGANISATION_WAIT_MAX_ATTEMPTS } from '@/constants';

  import { Floor as ApiFloor, Apartment as ApiApartment } from '@/types/api/tenants';
  import { SyncApartment as ApiSyncApartment, SyncFloor } from '@/types/api/sync';
  import { Apartment, Floor } from '@/types/widgets/tenantsWidget/tenantsWidget';

  import { apiGetDetailedAddress, apiGetSyncApartments } from '@/api/tenants';
  import { divideArray } from '@/helpers';
  import { getRBGAColor, hex8ToRgba, isHex8 } from '@/helpers/utils';
  import {
    resolveTenantName,
    filterApartmentsByContract,
  } from '@/helpers/widgets/tenantsWidget/tenantsWidget';
  import { logger } from '@/utils/screenEventsLogger';

  const props = defineProps({
    widgetData: { type: Object, default: {} },
  });

  const timer = ref<ReturnType<typeof setInterval> | null>(null);
  const currentPage = ref<number>(1);
  const isLoading = ref(false);
  const errorLoading = ref(false);
  const fetchedFloors = ref<ApiFloor[] | SyncFloor[]>([]);
  const refreshTimer = ref<ReturnType<typeof setInterval> | null>(null);
  const initialLoad = ref<boolean>(true);
  const skipInitialAnimation = ref<boolean>(true);
  const store = useStore();

  const tenantConfig = computed(() => {
    return store.getters.getTenantIntegrationConfig;
  });

  const syncTenantConfig = computed(() => {
    return store.getters.getSyncTenantsConfig;
  });

  const useStairwellNames = computed(() => {
    const nameSource =
      tenants.value.nameSource ?? syncTenantConfig.value?.nameSource ?? NAME_SOURCE.STAIRWELL;

    return nameSource === NAME_SOURCE.STAIRWELL;
  });

  const useSyncTenantsWidget = computed(() => {
    return store.getters.useSyncTenantsWidget;
  });

  const reverseSort = computed(() => {
    return useSyncTenantsWidget.value
      ? store.getters.getSyncTenantsConfig?.reverseSort
      : store.getters.getTenantIntegrationConfig?.reverseSort;
  });

  const { tenants } = toRefs(props.widgetData);
  const slideInterval = toRef(props.widgetData.tenants, 'interval');
  const allFloorsVisible = toRef(props.widgetData.tenants, 'allFloorsVisible');

  watch([slideInterval, allFloorsVisible], () => {
    resetSlide();
  });

  const containerStyle = computed(() => {
    return {
      paddingTop: `${tenants.value.topPadding || 20}px`,
    };
  });

  const containerClass = computed(() => {
    const amountOfColumns = tenants.value.amountOfColumns || (tenants.value.verticalMode ? 1 : 2);

    return `tenants-grid-${amountOfColumns}-col`;
  });

  const apartmentsContainerStyle = computed(() => {
    const justifyContent = !tenants.value.groupNamesAndNumbers
      ? 'space-between'
      : tenants.value.textAlignment;

    return {
      flexDirection: !tenants.value.numbersOnTheLeft ? 'row' : 'row-reverse',
      justifyContent,
      alignItems: tenants.value.rowVerticalAlignment ?? ROW_VERTICAL_ALIGN.TOP,
    };
  });

  const apartmentsWrapperStyle = computed(() => {
    return {
      paddingTop:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
      paddingBottom:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
    };
  });

  const wrapperStyle = computed(() => {
    return {
      backgroundColor: getRBGAColor(
        tenants.value.backgroundColor,
        Number(tenants.value.opacity) / 100,
      ),
    };
  });

  const titleStyle = computed(() => {
    const fontColor = tenants.value.titleFontColor;
    const bgColor = tenants.value.titleBackgroundColor;

    return {
      fontFamily: tenants.value.titleFont,
      fontSize: `${tenants.value.titleFontSize}px`,
      lineHeight: `${tenants.value.titleFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
      textAlign: tenants.value.titleAlignment || ALIGN_MODES.LEFT_ALIGN,
    };
  });

  const addressTitleStyle = computed(() => {
    const addressFont = tenants.value.addressFont || 'Arial';
    const addressFontSize = tenants.value.addressFontSize || 24;
    const addressFontColor = tenants.value.addressFontColor || '#000000';
    const addressBackgroundColor = tenants.value.addressBackgroundColor || 'transparent';
    const addressAlignment = tenants.value.addressAlignment || ALIGN_MODES.LEFT_ALIGN;

    const verticalPadding =
      tenants.value.addressVerticalPadding !== undefined &&
      tenants.value.addressVerticalPadding !== null
        ? tenants.value.addressVerticalPadding
        : 10;

    const horizontalPadding =
      tenants.value.addressHorizontalPadding !== undefined &&
      tenants.value.addressHorizontalPadding !== null
        ? tenants.value.addressHorizontalPadding
        : 20;

    return {
      display: tenants.value.showAddress ? 'block' : 'none',
      fontFamily: addressFont,
      fontSize: addressFontSize + 'px',
      lineHeight: addressFontSize * 1.3 + 'px',
      color:
        addressFontColor && isHex8(addressFontColor)
          ? hex8ToRgba(addressFontColor)
          : addressFontColor,
      backgroundColor:
        addressBackgroundColor && isHex8(addressBackgroundColor)
          ? hex8ToRgba(addressBackgroundColor)
          : addressBackgroundColor,
      textAlign: addressAlignment,
      paddingTop: verticalPadding + 'px',
      paddingBottom: verticalPadding + 'px',
      paddingLeft: horizontalPadding + 'px',
      paddingRight: horizontalPadding + 'px',
    };
  });

  const tenantNameStyle = computed(() => {
    const fontColor = tenants.value.floorFontColor;

    return {
      fontFamily: tenants.value.floorFont,
      fontSize: `${tenants.value.floorFontSize}px`,
      lineHeight: `${tenants.value.floorFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.textAlignment || ALIGN_MODES.LEFT_ALIGN,
      width: !tenants.value.groupNamesAndNumbers ? '100%' : 'auto',
    };
  });

  const tenantNumberStyle = computed(() => {
    const fontColor = tenants.value.numberFontColor;

    return {
      fontFamily: tenants.value.roomsFont,
      fontSize: `${tenants.value.numberFontSize}px`,
      lineHeight: `${tenants.value.numberFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.numberAlignment || ALIGN_MODES.LEFT_ALIGN,
      display: !tenants.value.hideNumbers ? 'block' : 'none',
    };
  });

  const externalIdStyle = computed(() => {
    const fontColor = tenants.value.numberFontColor;

    return {
      fontFamily: tenants.value.roomsFont,
      fontSize: `${tenants.value.numberFontSize}px`,
      lineHeight: `${tenants.value.numberFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.numberAlignment || ALIGN_MODES.LEFT_ALIGN,
      display: tenants.value.showExternalId ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: JUSTIFY_MODES[tenants.value.numberAlignment] || ALIGN_MODES.LEFT_ALIGN,
    };
  });

  const paginationStyle = computed(() => {
    const fontColor = tenants.value.paginationFontColor;

    return {
      fontFamily: tenants.value.paginationFont,
      fontSize: `${tenants.value.paginationFontSize}px`,
      lineHeight: `${tenants.value.paginationFontSize * 1.2}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      justifyContent: tenants.value.paginationAlignment || ALIGN_MODES.CENTER_ALIGN,
      height: tenants.value.paginationInText
        ? `${tenants.value.paginationFontSize + 16}px`
        : 'auto',
    };
  });

  const getPaginationDotStyle = (index: number) => {
    const fontColor = tenants.value.paginationFontColor;
    const isCurrentPage = index === currentPage.value;

    if (!isCurrentPage) {
      return { backgroundColor: 'auto' };
    }

    return {
      backgroundColor: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
    };
  };

  const pages = computed<Floor[][]>(() => {
    if (!tenants.value.floors) return [];

    let filteredFloors = tenants.value.allFloorsVisible
      ? tenants.value.floors
      : tenants.value.floors.filter(function (floor: Floor) {
          return floor && floor.visible !== false;
        });

    if (reverseSort.value === true && tenants.value.hasCustomOrder !== true) {
      filteredFloors = filteredFloors.slice(0).sort(function (a: Floor, b: Floor) {
        return b.floor - a.floor;
      });
    }

    return divideArray(filteredFloors, tenants.value.floorsOnSlide);
  });

  const tenantsAddressText = computed(() => {
    if (tenants.value.addressText) return tenants.value.addressText;
    if (tenants.value.address && tenants.value.address.name) return tenants.value.address.name;

    return 'Hyresgäster';
  });

  function setCurrentPage(value: number) {
    currentPage.value = value;
  }

  function nextTenantsSlide() {
    currentPage.value = currentPage.value < pages.value.length ? currentPage.value + 1 : 1;
  }

  function destroyTimer() {
    if (!timer.value) return;

    clearInterval(timer.value);
  }

  function resetSlide() {
    destroyTimer();
    setCurrentPage(1);

    if (pages.value.length <= 1) {
      return;
    }

    if (initialLoad.value) {
      initialLoad.value = false;
      setTimeout(() => {
        skipInitialAnimation.value = false;

        nextTenantsSlide();

        timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
      }, slideInterval.value * 1000);
    } else {
      skipInitialAnimation.value = false;
      timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
    }
  }

  async function fetchTenantsData(attempt = 0) {
    if (tenants.value.address && tenants.value.address.id === 'demo') return;

    errorLoading.value = false;
    isLoading.value = true;

    const organisationLoaded = store.getters.isOrganisationLoaded;

    if (!organisationLoaded && attempt < ORGANISATION_WAIT_MAX_ATTEMPTS) {
      setTimeout(() => fetchTenantsData(attempt + 1), ORGANISATION_WAIT_INTERVAL_MS);
      return;
    }

    useSyncTenantsWidget.value ? await fetchSyncTenants() : await fetchTenants();

    isLoading.value = false;
  }

  async function fetchTenants() {
    try {
      const addressId =
        tenants.value.address && tenants.value.address.id ? tenants.value.address.id : null;
      if (!addressId) {
        throw new Error('No address ID provided');
      }

      const response = await apiGetDetailedAddress(addressId);
      const floors = response && response.floors ? response.floors : null;

      if (!floors || !Array.isArray(floors)) {
        // No floors data received, keeping existing data
        fetchedFloors.value = tenants.value.floors;
        return;
      }

      tenants.value.floors = tenants.value.floors.map(function (floor: Floor) {
        let currentFloor = null;
        for (let i = 0; i < floors.length; i++) {
          if (floors[i].floor === floor.floor) {
            currentFloor = floors[i];
            break;
          }
        }

        let displayFloorName = null;
        if (floor && floor.displayFloorName) {
          displayFloorName = floor.displayFloorName;
        }

        const apartments = (
          currentFloor && currentFloor.apartments ? currentFloor.apartments : []
        ).map((apt: ApiApartment) => {
          // We need a reliable apartmentId: so it uses apartmentNumber if valid, otherwise externalId
          const apartmentId =
            apt.apartmentNumber &&
            apt.apartmentNumber !== '-' &&
            apt.apartmentNumber !== '' &&
            apt.apartmentNumber !== null &&
            apt.apartmentNumber !== undefined
              ? apt.apartmentNumber
              : apt.externalId;

          const existingApt = floor.apartments
            ? floor.apartments.find((apartment) => apartment.apartmentId === apartmentId)
            : null;

          return {
            apartmentNumber: apt.apartmentNumber,
            externalId: apt.externalId,
            names: apt.names || [],
            apartmentId: apartmentId,
            visible: existingApt && existingApt.visible !== undefined ? existingApt.visible : true,
            customNames: existingApt && existingApt.customNames ? existingApt.customNames : [],
            order: existingApt && existingApt.order !== undefined ? existingApt.order : null,
          };
        });

        let sortedApartments = apartments.slice(0);
        let hasOrder = false;
        for (let i = 0; i < sortedApartments.length; i++) {
          if (sortedApartments[i].order !== null) {
            hasOrder = true;
            break;
          }
        }

        if (hasOrder) {
          sortedApartments.sort(function (a: Apartment, b: Apartment) {
            if (a.order === null) return 1;
            if (b.order === null) return -1;
            return Number(a.order) - Number(b.order);
          });
        }

        const newFloor = Object.assign({}, floor);
        newFloor.apartments = sortedApartments;

        newFloor.visible = floor && floor.visible === false ? false : true;

        newFloor.displayFloorName = displayFloorName || '';

        return newFloor;
      });

      fetchedFloors.value = floors;
    } catch (error: unknown) {
      logger.addLog('Error fetching floors: ' + error);

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // No floors data received, keeping existing data
        fetchedFloors.value = tenants.value.floors;
        return;
      }

      errorLoading.value = true;
    }
  }

  function getExternalId(externalId: string) {
    if (!externalId) return '';

    const parts = externalId.split('-');

    if (parts.length <= 1) return externalId;

    return parts.slice(1).join('-');
  }

  async function fetchSyncTenants() {
    try {
      const response = await apiGetSyncApartments(tenants.value.address?.id);
      const syncFloors = response && response.floors ? response.floors : null;

      if (!syncFloors || !Array.isArray(syncFloors)) {
        // No sync floors data received, keeping existing data
        fetchedFloors.value = tenants.value.floors;

        return;
      }

      const updatedFloors: Floor[] = syncFloors.map(function (syncFloor: SyncFloor) {
        const floor = tenants.value.floors.find((f: Floor) => f.floor === syncFloor.floor) || {};

        const apartmentOrder = floor.apartmentOrder || {};

        const apartments = (syncFloor.apartments || []).map((syncApt: ApiSyncApartment) => {
          const apartmentId = syncApt.apartmentNumber?.trim()
            ? syncApt.apartmentNumber
            : syncApt.externalId || syncApt.contractId;

          const existingApt = floor?.apartments?.find(
            (apartment: Apartment) => apartment.apartmentId === apartmentId,
          );

          let order: number | null = null;

          if (existingApt?.order !== undefined) {
            order = existingApt?.order;
          }

          if (!order && apartmentId && apartmentOrder[apartmentId] !== undefined) {
            order = apartmentOrder[apartmentId];
          }

          return {
            ...syncApt,
            apartmentId,
            visible: existingApt?.visible !== false,
            customNames: existingApt?.customNames || [],
            order,
          };
        });

        const sortedApartments = [...apartments];

        if (sortedApartments.some((apt) => apt.order !== null)) {
          sortedApartments.sort((a, b) => {
            if (a.order === null) return 1;
            if (b.order === null) return -1;
            return a.order - b.order;
          });
        }

        return {
          floor: syncFloor.floor,
          displayFloorName: floor.displayFloorName,
          displayFloor: syncFloor.displayFloor,
          apartments: sortedApartments,
          visible: floor && floor.visible === false ? false : true,
          apartmentOrder: floor.apartmentOrder,
        };
      });

      let orderedFloors = updatedFloors;

      if (tenants.value.hasCustomOrder) {
        const floorsByNumber = new Map<number, Floor>(
          updatedFloors.map((f: Floor) => [f.floor, f]),
        );

        const knownFloorNumbers = new Set<number>();

        const floorsInCustomOrder = tenants.value.floors
          .map((f: Floor) => {
            knownFloorNumbers.add(f.floor);
            return floorsByNumber.get(f.floor);
          })
          .filter((f: Floor | undefined): f is Floor => Boolean(f));

        const newFloorsNotInCustomOrder = updatedFloors.filter(
          (f: Floor) => !knownFloorNumbers.has(f.floor),
        );

        orderedFloors = floorsInCustomOrder.concat(newFloorsNotInCustomOrder);
      }

      tenants.value.floors = orderedFloors;

      fetchedFloors.value = syncFloors;
    } catch (error) {
      logger.addLog('FetchSyncTenants - error: ' + error);

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // No floors data received, keeping existing data
        fetchedFloors.value = tenants.value.floors;
        return;
      }

      errorLoading.value = true;
    }
  }

  const prepareSyncName = (apartment: Apartment) => {
    const separator =
      tenants.value.separator ??
      syncTenantConfig.value?.separator ??
      DEFAULT_TENANTS_NAME_SEPARATOR;

    if (apartment.customNames && apartment.customNames.length > 0) {
      return apartment.customNames.join(separator);
    }

    if (useStairwellNames.value) {
      const stairwellNames = apartment.stairwellNames as string[] | undefined;

      if (stairwellNames && stairwellNames.length > 0) {
        return stairwellNames.join(separator);
      }
    }

    const nameFormat =
      tenants.value.nameFormat ?? syncTenantConfig.value?.nameFormat ?? NAME_FORMAT.F_LAST;
    const showTwoNames =
      tenants.value.showTwoNames ?? syncTenantConfig.value?.showTwoNames ?? false;
    const valueForEmptyName =
      tenants.value.valueForEmptyName ?? syncTenantConfig.value?.valueForEmptyName ?? '';

    const name1 = resolveTenantName(
      apartment.tenant1FirstName,
      apartment.tenant1LastName,
      apartment.tenant1FullName,
      nameFormat,
      valueForEmptyName,
    );

    const noSecondName =
      !apartment.tenant2FirstName && !apartment.tenant2LastName && !apartment.tenant2FullName;

    if (!name1 && apartment.names && apartment.names.length > 0) {
      return apartment.names.join(separator);
    }

    if (!showTwoNames || noSecondName) return name1;

    const name2 = resolveTenantName(
      apartment.tenant2FirstName,
      apartment.tenant2LastName,
      apartment.tenant2FullName,
      nameFormat,
      valueForEmptyName,
    );

    if (!name2) return name1;

    return [name1, name2].join(separator);
  };

  const prepareName = (apartment: Apartment) => {
    const namesToUse =
      apartment.customNames && apartment.customNames.length > 0
        ? apartment.customNames
        : apartment.names || [];

    const config = tenantConfig.value;
    const separator = config && config.separator ? config.separator : ', ';

    if (!tenants.value.shortenNames) {
      return namesToUse.join(separator);
    }

    const shortenedNames = [];
    for (let i = 0; i < namesToUse.length; i++) {
      const name = namesToUse[i];
      const words = name.split(' ');

      if (words.length < 2) {
        shortenedNames.push(name);
      } else {
        const firstNameInitial = words[0][0] + '.';
        const lastName = words[words.length - 1];
        shortenedNames.push(firstNameInitial + ' ' + lastName);
      }
    }

    return shortenedNames.join(separator);
  };

  onMounted(async () => {
    resetSlide();
    await fetchTenantsData();

    if (refreshTimer.value) {
      clearInterval(refreshTimer.value);
    }

    if (tenants.value.refreshTime && tenants.value.refreshTime > 0) {
      const refreshInterval = duration(
        tenants.value.refreshTime || 24,
        tenants.value.timeUnit || 'hours',
      ).asMilliseconds();

      refreshTimer.value = setInterval(fetchTenantsData, refreshInterval);
    }
  });

  const getFormattedFloorName = (floor: Floor) => {
    if (floor.displayFloorName) {
      return floor.displayFloorName;
    }

    const useDisplayFloorAsFloorNumber = tenants.value.useDisplayFloorAsFloorNumber;

    const caps = useSyncTenantsWidget.value
      ? syncTenantConfig.value && syncTenantConfig.value.caps
      : (tenantConfig.value && tenantConfig.value.caps) || false;

    const floorNameType = useSyncTenantsWidget.value
      ? (syncTenantConfig.value && syncTenantConfig.value.floorNameType) || FLOOR_NAME_TYPES.VANING
      : (tenantConfig.value && tenantConfig.value.floorNameType) || FLOOR_NAME_TYPES.VANING;

    const usesDisplayValue = useDisplayFloorAsFloorNumber === FLOOR_NUMBER_MODE.DISPLAY_VALUE;
    const floorNumber = usesDisplayValue && floor.displayFloor ? floor.displayFloor : floor.floor;
    let floorName = '';

    switch (floorNameType) {
      case FLOOR_NAME_TYPES.PLAN:
        floorName = `Plan ${floorNumber}`;
        break;
      case FLOOR_NAME_TYPES.NUMBERS:
        floorName = `${floorNumber}`;
        break;
      case FLOOR_NAME_TYPES.VANING:
      default:
        floorName = `Våning ${floorNumber}`;
        break;
    }

    if (
      floor.floor === 0 ||
      (floor.displayFloor &&
        (floor.displayFloor.toLowerCase() === 'bv' ||
          floor.displayFloor.toLowerCase() === 'bottenvåning'))
    ) {
      floorName = 'Bottenvåning';
    }

    return caps ? floorName.toUpperCase() : floorName;
  };

  const filterFloorsApartments = (floor: Floor) => {
    const visibleFloors = (floor.apartments as Apartment[]).filter(
      (apt: Apartment) => apt.visible !== false,
    );

    if (!useSyncTenantsWidget.value || tenants.value.address?.id === 'demo') {
      return visibleFloors;
    }

    const contractDays = syncTenantConfig.value?.contractDays || 0;
    const showAllApartments = syncTenantConfig.value?.rentalStatus !== RENTAL_STATUS.RENTED;

    return filterApartmentsByContract(
      visibleFloors,
      contractDays,
      showAllApartments,
      floor.apartmentOrder,
    );
  };

  onUnmounted(() => {
    destroyTimer();
    if (refreshTimer.value) clearInterval(refreshTimer.value);
  });
</script>

<template>
  <div v-if="isLoading && (!fetchedFloors || !fetchedFloors.length)" class="loader-container">
    <div class="loader-title">Loading Tenants...</div>
    <Loader />
  </div>

  <div v-else-if="errorLoading" class="error-message">Data not available</div>

  <div v-else-if="fetchedFloors" class="tenants-wrapper" :style="wrapperStyle">
    <div v-if="tenants.showAddress" class="address-title" :style="addressTitleStyle">
      {{ tenants.addressText || (tenants.address && tenants.address.name) }}
    </div>

    <div class="tenants-container">
      <div
        v-for="(page, idx) in pages"
        :class="[
          'tenants-page',
          containerClass,
          {
            slideIn: idx === currentPage - 1 && !skipInitialAnimation,
            slideOut: idx !== currentPage - 1 && !skipInitialAnimation,
            'initial-slide': idx === currentPage - 1 && skipInitialAnimation,
            'hidden-slide': idx !== currentPage - 1 && skipInitialAnimation,
          },
        ]"
        :key="idx"
        :style="containerStyle"
      >
        <div v-for="(floor, index) in page" :key="index" class="floor">
          <div class="floor-title" :style="titleStyle">
            {{ getFormattedFloorName(floor) }}
          </div>

          <div
            v-for="(tenant, tenantIndex) in filterFloorsApartments(floor)"
            :key="`${floor.floor}-${tenant.apartmentId || tenant.externalId || tenantIndex}`"
            class="tenants-apartment-wrapper"
            :style="apartmentsWrapperStyle"
          >
            <div
              class="tenants"
              :style="apartmentsContainerStyle"
              :class="
                apartmentsContainerStyle.flexDirection === 'row-reverse'
                  ? 'style-number-first'
                  : 'style-name-first'
              "
            >
              <div class="tenant-name" :style="tenantNameStyle">
                {{ useSyncTenantsWidget ? prepareSyncName(tenant) : prepareName(tenant) }}
              </div>
              <div class="tenant-number" :style="tenantNumberStyle">
                {{ tenant.apartmentNumber }}
              </div>
            </div>

            <div v-if="tenant.externalId" class="tenant-external-id" :style="externalIdStyle">
              {{ getExternalId(tenant.externalId) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="slides scrollbar" :style="paginationStyle" v-if="pages.length > 1">
      <template v-if="!widgetData.tenants.paginationInText">
        <div
          v-for="index in pages.length"
          :key="index"
          class="page-dot"
          @click="setCurrentPage(index)"
        >
          <div
            :class="index === currentPage ? 'current-dot' : 'dot'"
            :style="getPaginationDotStyle(index)"
          ></div>
        </div>
      </template>
      <div v-else class="pagination-in-text">
        {{ `Sida ${currentPage}/${pages.length}` }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tenants-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding-bottom: 10px;
  }

  .tenants-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    flex: 1;
  }

  .address-title {
    width: 100%;
    overflow-wrap: break-word;
  }

  .tenants-page {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    -webkit-transform: translateX(-100%);
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    padding-right: 20px;
    padding-left: 20px;
    padding-bottom: 0;

    .floor {
      margin-bottom: 24px;
      margin-right: 24px;
    }

    &.tenants-grid-1-col .floor {
      width: 100%;
      margin-right: 0;
    }

    &.tenants-grid-2-col .floor {
      width: calc(50% - 12px);

      &:nth-child(2n) {
        margin-right: 0;
      }
    }

    &.tenants-grid-3-col .floor {
      width: calc(33.333% - 16px);

      &:nth-child(3n) {
        margin-right: 0;
      }
    }

    &.tenants-grid-4-col .floor {
      width: calc(25% - 18px);

      &:nth-child(4n) {
        margin-right: 0;
      }
    }

    &.tenants-grid-5-col .floor {
      width: calc(20% - 19.2px);

      &:nth-child(5n) {
        margin-right: 0;
      }
    }

    &.tenants-grid-6-col .floor {
      width: calc(16.666% - 20px);

      &:nth-child(6n) {
        margin-right: 0;
      }
    }

    .tenants-apartment-wrapper {
      display: flex;
      flex-direction: row;
      width: 100%;
    }

    .tenants {
      display: flex;
      align-items: flex-start;
      padding-left: 12px;
      padding-right: 12px;
      flex-grow: 1;
      column-gap: 12px;

      &.style-number-first {
        .tenant-number {
          margin-right: 12px;
        }
      }
      &.style-name-first {
        .tenant-name {
          margin-right: 12px;
        }
      }
    }

    .floor-title {
      width: 100%;
      font-weight: bold;
      padding: 9px 11px;
      border-radius: 8px;
    }

    .tenant-number {
      font-weight: bold;
      width: 52px;
    }

    .tenant-external-id {
      font-weight: bold;
      padding: 0 12px;
      width: max-content;
      min-width: 60px;
    }
  }

  .loader-container,
  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    padding: 16px;
    row-gap: 16px;

    .loader-title {
      font-size: 16px;
      text-align: center;
    }

    .loading-placeholder {
      height: 40px;
    }
  }

  .error-message {
    font-size: 16px;
    text-align: center;
    color: #151515;
  }

  .slideIn {
    animation: slide-in 1s forwards;
    -webkit-animation: slide-in 1s forwards;
  }

  .slideOut {
    animation: slide-out 1s forwards;
    -webkit-animation: slide-out 1s forwards;
  }

  .initial-slide {
    transform: translateX(0%) !important;
    -webkit-transform: translateX(0%) !important;
  }

  .hidden-slide {
    transform: translateX(-100%) !important;
    -webkit-transform: translateX(-100%) !important;
  }

  @keyframes slide-in {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @-webkit-keyframes slide-in {
    100% {
      -webkit-transform: translateX(0%);
    }
  }

  @keyframes slide-out {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(-100%);
    }
  }

  @-webkit-keyframes slide-out {
    0% {
      -webkit-transform: translateX(0%);
    }
    100% {
      -webkit-transform: translateX(-100%);
    }
  }

  .slides {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 20px;
    width: 100%;
    flex-wrap: wrap;

    .page-dot {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 34px;
      height: 20px;
      border-radius: 100%;
      padding: 3px 10px;

      &:hover {
        cursor: pointer;

        .dot {
          background-color: #151515;
          opacity: 40%;
        }
      }
    }

    .dot,
    .current-dot {
      background-color: #151515;
      border-radius: 100%;
    }

    .dot {
      opacity: 20%;
      width: 8px;
      height: 8px;
    }

    .current-dot {
      background-color: #151515;
      width: 14px;
      height: 14px;
    }
  }
</style>
